import { memo } from 'react';
import {
  Typography,
  ListItemButton,
  ListItemText,
  Collapse,
  ListItemIcon,
  Box,
  alpha,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { designTokens } from '@design-system';
import { useAppSelector } from '@/store/hooks';
import IconRetrieve from '../../IconRetrieve';

interface MenuGroupProps {
  children: any;
  item: any;
  level: number;
  key: string;
  isOpen: boolean;
  isActive: boolean;
  onToggle: (id: string, items: any) => void;
}

const Groups = memo<MenuGroupProps>(function Groups({
  children,
  item,
  level,
  isOpen,
  isActive,
  onToggle,
}) {
  const menu = useAppSelector((state) => state.menu);
  const { drawerOpen } = menu;

  return (
    <>
      <ListItemButton
        onClick={() => onToggle(item.id, item)}
        // keyboard navigation of the menu (Navigation/index.tsx)
        data-nav-id={item.id}
        data-nav-kind="group"
        data-nav-level={level}
        data-nav-open={isOpen ? 'Y' : 'N'}
        aria-expanded={isOpen}
        sx={{
          zIndex: 1201,
          pl: drawerOpen ? `${level * 10 + 10}px` : 1.25,
          pr: 1.5,
          py: 1.15,
          minHeight: level === 1 ? 46 : 40,
          borderRadius: '9px',
          mx: 0.75,
          mb: 0.35,
          transition: `all ${designTokens.transitions.duration.normal} ${designTokens.transitions.timing.easeInOut}`,
          backgroundColor: isActive
            ? alpha(designTokens.colors.primary[500], 0.08)
            : 'transparent',
          border: isActive
            ? `1px solid ${alpha(designTokens.colors.primary[500], 0.25)}`
            : '1px solid transparent',
          boxShadow: isActive
            ? `inset 3px 0 0 ${designTokens.colors.primary[500]}`
            : 'inset 3px 0 0 transparent',
          '&:hover': {
            backgroundColor: alpha(designTokens.colors.primary[500], 0.12),
            borderColor: alpha(designTokens.colors.primary[500], 0.2),
          },
          // keyboard focus must be plain to see (menu is fully keyboard-driven)
          '&.Mui-focusVisible, &:focus-visible': {
            backgroundColor: alpha(designTokens.colors.primary[500], 0.18),
            outline: `2px solid ${designTokens.colors.primary[400]}`,
            outlineOffset: '-2px',
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 34,
            color: isActive
              ? designTokens.colors.primary[500]
              : designTokens.colors.neutral[400],
            transition: `color ${designTokens.transitions.duration.fast}`,
          }}
        >
          {level === 1 ? (
            <IconRetrieve
              icon={item.icon || 'Icon' + item.title.replace(/\s+|\.*$/g, '')}
              open={isActive}
            />
          ) : (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: isActive
                  ? designTokens.colors.primary[500]
                  : designTokens.colors.neutral[500],
                transition: `all ${designTokens.transitions.duration.fast}`,
              }}
            />
          )}
        </ListItemIcon>

        <ListItemText
          // long section names ("Human Resource Management") must not run
          // under the chevron: the label truncates, the chevron keeps its slot
          sx={{ minWidth: 0, mr: 0.5 }}
          primary={
            <Typography
              variant={level === 1 ? 'body2' : 'caption'}
              title={item.title}
              sx={{
                fontFamily: 'inherit',
                fontWeight: isActive ? 600 : 500,
                color: isActive
                  ? designTokens.colors.neutral[50]
                  : designTokens.colors.neutral[300],
                letterSpacing: '0.01em',
                transition: `all ${designTokens.transitions.duration.fast}`,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item.title}
            </Typography>
          }
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: 22,
            height: 22,
            borderRadius: '5px',
            backgroundColor: isOpen
              ? alpha(designTokens.colors.primary[500], 0.15)
              : 'transparent',
            transition: `all ${designTokens.transitions.duration.normal}`,
          }}
        >
          <KeyboardArrowDownRoundedIcon
            fontSize="small"
            sx={{
              color: isOpen
                ? designTokens.colors.primary[500]
                : designTokens.colors.neutral[500],
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: `transform ${designTokens.transitions.duration.normal} ${designTokens.transitions.timing.easeInOut}`,
            }}
          />
        </Box>
      </ListItemButton>

      <Collapse in={isOpen} timeout={300} unmountOnExit>
        <Box
          sx={{
            ml: level === 1 ? 2 : 2.25,
            pl: 0.75,
            borderLeft: `2px solid ${alpha(designTokens.colors.primary[500], 0.2)}`,
            my: 0.35,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </>
  );
});

Groups.displayName = 'Groups';

export default Groups;
